import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Pencil, Trash2, X, type LucideIcon } from 'lucide-react';
import type { ViewportEngine } from '../../viewer/ViewportEngine';
import { invertAffine, multiplyAffine, probeAffine, sameAffine, svgMatrix, type Affine } from '../../viewer/affine';
import type { AnnPoint, Annotation, AnnotationPatch, NewAnnotation } from '../../annotation/types';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AnnotationByline, annotationAt } from './annotationHover';
import {
  COUNTER_PALETTE,
  arrowLabel,
  colorForStyle,
  counterLabel,
  formatArea,
  formatLength,
  isSmoothPolygon,
  polygonStyle,
  measureLabel,
} from '../../annotation/style';
import {
  bboxCorners,
  cornersFrom,
  ellipsePoints,
  labelOutside,
  pointsEqual,
  polygonArea,
  polygonCentroid,
  rectFrame,
  resizeCorner,
  rotatePointsAround,
  smoothClosedPoints,
} from '../../annotation/geometry';

/** Which annotation tool is armed. 'none' = select/inspect mode. */
export type AnnotationTool =
  | 'none'
  | 'measure'
  | 'arrow'
  | 'rectangle'
  | 'ellipse'
  | 'polygon'
  | 'counter';

/** The in-progress counting session: a new group (id null) or an existing
 *  counter being appended to. Points live here until persisted on switch/exit. */
export interface CounterSession {
  id: string | null;
  colorIndex: number;
  points: AnnPoint[];
}

export interface AnnotationOverlayProps {
  engine: ViewportEngine;
  /** The study's public id (`stu_…`), which new annotations are filed under. */
  studyId: string;
  seriesInstanceUID: string;
  frameOfReferenceUID?: string;
  tool: AnnotationTool;
  /** New polygons are drawn smoothed (spline) when true, straight when false. */
  polygonSmooth?: boolean;
  annotations: Annotation[];
  /** Create an annotation; resolves to the saved record (its real id is what
   *  naming it needs), or to nothing when the server refused it. */
  onCreate: (a: NewAnnotation) => void | Promise<Annotation | undefined>;
  onUpdate: (id: string, patch: AnnotationPatch) => void;
  onDelete: (id: string) => void;
  /** Leave the active tool (Esc), back to select/inspect mode. */
  onExitTool?: () => void;
  /** Active counting session (owned by the parent). */
  session?: CounterSession | null;
  /** Add a point to the active counting session. */
  onCounterAdd?: (p: AnnPoint) => void;
  /** Remove a point (by index) from the active counting session. */
  onCounterRemovePoint?: (index: number) => void;
  /** Delete the active counting session's counter (Delete key while counting). */
  onDeleteSession?: () => void;
  /** An annotation to open the name field on once it is on the slide: a count
   *  just finished, which the parent saves. */
  nameRequest?: string | null;
  /** The name field has opened for `nameRequest`. */
  onNameRequestTaken?: () => void;
  /**
   * Display only, for somebody who may view the slide but not annotate it:
   * nothing can be selected, dragged, renamed or deleted, so none of the
   * handles or buttons that would do it ever appear. Somebody else's
   * annotation (`isYou` not set) is shown this way on any slide.
   */
  readOnly?: boolean;
  /**
   * The annotation hovered, here or on its row in the Annotations list: drawn
   * highlighted. The page keeps it, so the two hovers are one (user,
   * 2026-10-10). Without it the overlay highlights its own hover.
   */
  hoveredId?: string | null;
  /** The pointer has come onto an annotation on the slide, or left it (null). */
  onHover?: (id: string | null) => void;
  /**
   * This slide is the one being worked on: compare's selected pane, and the
   * single view always. A pane left for another drops the shape it was
   * drawing — a shape is on one slide, so a polygon begun on A and continued
   * on B is abandoned on A, and Enter finishes only the one on B.
   */
  active?: boolean;
}

const HALO = 'rgba(0,0,0,0.7)'; // dark underlay so strokes read on bright tissue

// A region's label (name and area) sits outside it, never over the tissue it
// encloses (labelOutside), and a counter's above its dots. Half the pill's
// height (Badge), and its distance from the outline: clear of the halo, on a
// rectangle or ellipse clear of the width and height readouts straddling its
// edges too, and on a counter clear of the dots (r 5 when hovered or selected).
const BADGE_HALF_H = 9;
const POLYGON_LABEL_GAP = 6;
const BOX_LABEL_GAP = 11;
const COUNTER_LABEL_GAP = 8;

// A point on screen (viewport px), as the map places an image point: null off it.
type Pt = { x: number; y: number };
const isPoint = (s: Pt | null | undefined): s is Pt => !!s;

// How far the name field keeps from the view's edge when its label is out of view.
const NAME_FIELD_INSET = 4;

// How far (viewport px) a press has to travel before letting go ends the shape
// there; any less is a click.
const DRAG_THRESHOLD_PX = 4;

// How long the view stays still after moving before an annotation's hover
// tooltip opens again (a wheel zoom's notches arrive closer than this).
const VIEW_STILL_MS = 300;

// The native OS resize cursor nearest to `angle` — crisp and familiar. Snaps to
// 45° (ew / nwse / ns / nesw), so it tracks the shape's rotation in steps.
function resizeCursor(angle: number): string {
  let deg = ((angle * 180) / Math.PI) % 180;
  if (deg < 0) deg += 180;
  return ['ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize'][Math.round(deg / 45) % 4];
}

// A rotate cursor (no native equivalent): a bold quarter-circle arc with a solid
// arrowhead merged into each end (drawn as a black halo layer + a white top layer
// so there's no seam between the arc and heads). Oriented by `angle` (+180° base
// offset so it sits aligned to the corner); snapped to 5° to limit re-parsing.
function rotateCursor(angle: number): string {
  const deg = Math.round(((angle * 180) / Math.PI + 225) / 5) * 5;
  const arc = 'M12 5 A 7 7 0 0 1 19 12';
  const heads = 'M7.5 5 L12 1.5 L12 8.5 Z M19 16.5 L16 12 L22 12 Z';
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke-linecap="round" stroke-linejoin="round">` +
    `<g transform="rotate(${deg} 12 12)">` +
    `<path stroke="black" stroke-width="5" d="${arc}"/>` +
    `<path fill="black" stroke="black" stroke-width="1.5" d="${heads}"/>` +
    `<path stroke="white" stroke-width="2.5" d="${arc}"/>` +
    `<path fill="white" d="${heads}"/></g></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 12 12, grab`;
}

// A compact dark pill for edge dimension readouts, rotated to run along its axis.
function MiniLabel({
  x,
  y,
  text,
  color,
  rotate = 0,
}: {
  x: number;
  y: number;
  text: string;
  color: string;
  rotate?: number;
}) {
  const w = text.length * 5.6 + 8;
  return (
    <g transform={`translate(${x}, ${y}) rotate(${rotate})`} style={{ pointerEvents: 'none' }}>
      <rect x={-w / 2} y={-8} width={w} height={16} rx={3} fill="rgba(11,11,11,0.82)" />
      <text
        x={0}
        y={0}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={10}
        fill={color}
        style={{ userSelect: 'none' }}
      >
        {text}
      </text>
    </g>
  );
}

// An in-flight drag. Measures/arrows move an endpoint ('point') or translate
// ('move'). Box shapes ('shape') carry a recompute() that maps the current image
// pointer to a fresh point set (move / corner-resize / rotate all fit this).
// Kept local so we don't PATCH on every pointermove.
type Drag =
  | { id: string; kind: 'point'; index: number; point: AnnPoint }
  | { id: string; kind: 'move'; start: AnnPoint; orig: AnnPoint[]; points: AnnPoint[] }
  | { id: string; kind: 'shape'; recompute: (d: AnnPoint) => AnnPoint[]; points: AnnPoint[] };

// Nearest point on a closed polyline to `cursor`, plus the index of the segment
// it landed on. Used to snap the polygon "+" indicator to the visible outline.
function nearestOnClosed(cursor: { x: number; y: number }, poly: { x: number; y: number }[]) {
  let best: { d: number; x: number; y: number; i: number } | null = null;
  for (let i = 0; i < poly.length; i++) {
    const A = poly[i];
    const B = poly[(i + 1) % poly.length];
    const abx = B.x - A.x;
    const aby = B.y - A.y;
    const t = Math.max(0, Math.min(1, ((cursor.x - A.x) * abx + (cursor.y - A.y) * aby) / (abx * abx + aby * aby || 1)));
    const x = A.x + abx * t;
    const y = A.y + aby * t;
    const d = Math.hypot(x - cursor.x, y - cursor.y);
    if (!best || d < best.d) best = { d, x, y, i };
  }
  return best;
}

// Normalize a label rotation to (-90, 90] so text stays on-axis but never upside
// down (a 180° flip keeps it on the same line, just readable).
function readableAngle(deg: number): number {
  let d = (((deg + 180) % 360) + 360) % 360 - 180;
  if (d > 90) d -= 180;
  else if (d < -90) d += 180;
  return d;
}

// A position along one axis of the view, at least `margin` px inside it (as
// it is when the view has no size yet).
function keepInView(v: number, size: number | undefined, margin: number): number {
  return size && size > 2 * margin ? Math.min(Math.max(v, margin), size - margin) : v;
}

// A single counter point — a small plain dot.
function CounterMarker({
  x,
  y,
  color,
  interactive,
  onClick,
}: {
  x: number;
  y: number;
  color: string;
  interactive: boolean;
  onClick?: () => void;
}) {
  return (
    <circle
      data-ann
      cx={x}
      cy={y}
      r={4}
      fill={color}
      stroke={HALO}
      strokeWidth={1.5}
      style={{ pointerEvents: interactive ? 'auto' : 'none', cursor: 'pointer' }}
      onPointerDown={
        interactive && onClick
          ? (e) => {
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
    />
  );
}

// A dark pill with centered text (measurement / counter tally). Click to select,
// double-click to rename.
function Badge({
  x,
  y,
  text,
  color,
  onSelect,
  onRename,
  hover,
  hot,
}: {
  x: number;
  y: number;
  text: string;
  color: string;
  onSelect?: () => void;
  onRename?: () => void;
  /** The annotation it names, for the hover over it (annotationHover.ts). */
  hover?: string;
  /** Its annotation is hovered, here or in the list: ringed in its colour. */
  hot?: boolean;
}) {
  const w = text.length * 6.4 + 14;
  const interactive = !!(onSelect || onRename);
  return (
    <g transform={`translate(${x}, ${y})`}>
      <rect
        {...(interactive ? { 'data-ann': '' } : {})}
        data-hover={hover}
        x={-w / 2}
        y={-BADGE_HALF_H}
        width={w}
        height={BADGE_HALF_H * 2}
        rx={4}
        fill="rgba(11,11,11,0.82)"
        stroke={hot ? color : undefined}
        strokeWidth={hot ? 1.5 : undefined}
        style={interactive ? { pointerEvents: 'auto', cursor: 'pointer' } : undefined}
        onPointerDown={onSelect ? (e) => { e.stopPropagation(); onSelect(); } : undefined}
        onDoubleClick={onRename ? (e) => { e.stopPropagation(); onRename(); } : undefined}
      />
      <text
        x={0}
        y={0}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={11}
        fill={color}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {text}
      </text>
    </g>
  );
}

// A small round icon button beside a label (rename / delete), offset by `x`.
function LabelButton({
  x,
  color,
  title,
  Icon,
  onClick,
}: {
  x: number;
  color: string;
  title: string;
  Icon: LucideIcon;
  onClick: () => void;
}) {
  return (
    <g
      data-ann
      transform={`translate(${x}, 0)`}
      style={{ pointerEvents: 'auto', cursor: 'pointer' }}
      // Act on the full click; firing on pointerdown lets the browser's mousedown
      // focus handling immediately blur the rename input it opens.
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <title>{title}</title>
      <circle r={10} fill="rgba(11,11,11,0.82)" stroke={color} strokeWidth={1} />
      <Icon x={-6.5} y={-6.5} width={13} height={13} color={color} />
    </g>
  );
}

export function AnnotationOverlay({
  engine,
  studyId,
  seriesInstanceUID,
  frameOfReferenceUID,
  tool,
  polygonSmooth = true,
  annotations,
  onCreate,
  onUpdate,
  onDelete,
  onExitTool,
  session,
  onCounterAdd,
  onCounterRemovePoint,
  onDeleteSession,
  nameRequest,
  onNameRequestTaken,
  readOnly = false,
  hoveredId,
  onHover,
  active = true,
}: AnnotationOverlayProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [, force] = useState(0);
  const rerender = () => force((n) => n + 1);

  const [pending, setPending] = useState<AnnPoint | null>(null);
  // Where the press drawing a two-point shape went down (viewport px), and
  // whether it placed the start (rather than being the second click).
  const pressRef = useRef<{ x: number; y: number; fresh: boolean } | null>(null);
  // Vertices placed so far for a polygon under construction (image space).
  const [poly, setPoly] = useState<AnnPoint[]>([]);
  const polyRef = useRef<AnnPoint[]>(poly);
  polyRef.current = poly;
  // Latest closePoly, so the keydown handler can call it without re-subscribing.
  const closePolyRef = useRef<(pts: AnnPoint[]) => void>(() => {});
  // Debounce for double-click-to-close (two quick clicks at the same spot).
  const lastClickRef = useRef<{ t: number; x: number; y: number } | null>(null);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null); // measure or counter
  const [drag, setDrag] = useState<Drag | null>(null);
  // Counter dot under the cursor (its delete affordance shows on hover).
  const [hoverDot, setHoverDot] = useState<{ id: string; index: number } | null>(null);
  // Point on a selected polygon's outline under the cursor: where the "+" shows
  // and a click inserts a vertex. `seg` is the control edge it belongs to.
  const [edgeHover, setEdgeHover] = useState<
    { id: string; x: number; y: number; seg: number } | null
  >(null);
  // Inline rename of a label (HTML input positioned over the SVG label).
  const [editing, setEditing] = useState<{ id: string; value: string; x: number; y: number } | null>(
    null,
  );
  // The name field's size on screen, measured once it is in the DOM and as it
  // grows with the name: what keeps the whole of it inside the view.
  const nameRef = useRef<HTMLInputElement>(null);
  const [nameSize, setNameSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = nameRef.current;
    const w = el?.offsetWidth ?? 0;
    const h = el?.offsetHeight ?? 0;
    setNameSize((s) => (s.w === w && s.h === h ? s : { w, h }));
  }, [editing]);
  // The annotation under the pointer, and where the pointer found it (host
  // px); `tip` is the annotation whose tooltip is open; `viewMoves` counts the
  // view's moves, which close it until the view is still, and `movedAtRef`
  // holds when the last one was.
  const [hover, setHover] = useState<{ id: string; x: number; y: number } | null>(null);
  const [tip, setTip] = useState<string | null>(null);
  const [viewMoves, setViewMoves] = useState(0);
  const movedAtRef = useRef(-Infinity);
  // The annotation hovered: the page's shared hover when it keeps one — the
  // pointer here, or on its row in the list — else this overlay's own. It is
  // drawn highlighted and its tooltip opens, wherever the hover came from.
  const hotId = hoveredId !== undefined ? hoveredId : (hover?.id ?? null);
  // Tell the page whenever the pointer comes onto an annotation or leaves it,
  // so its row in the list shows hovered too.
  const onHoverRef = useRef(onHover);
  onHoverRef.current = onHover;
  const hoverId = hover?.id ?? null;
  useEffect(() => {
    onHoverRef.current?.(hoverId);
  }, [hoverId]);
  // A hover is not looked for while drawing: the shapes under a tool are not
  // what the pointer is after.
  const drawingRef = useRef(false);
  drawingRef.current = tool !== 'none';

  const startEdit = (a: Annotation, x: number, y: number) =>
    setEditing({ id: a.id, value: a.text ?? '', x, y });
  // An annotation is its author's: somebody else's is shown and never touched,
  // as everything is on a read-only slide. The server refuses its every change,
  // so it is never selected, and none of the handles or buttons a selection
  // brings ever appear on it.
  const locked = (a: Annotation) => readOnly || !a.isYou;
  // An annotation that takes no pointer: under a tool being drawn with, on a
  // slide the person may only view, or somebody else's. Its label takes none
  // either — the labels sit above the drawing layer, so a shape begun on one
  // would select the annotation instead — and a label that takes none is
  // drawn as plain text.
  const inert = (a: Annotation) => tool !== 'none' || locked(a);
  const selectOn = (a: Annotation) => (inert(a) ? undefined : () => setSelectedId(a.id));
  const renameOn = (a: Annotation, x: number, y: number) => (inert(a) ? undefined : () => startEdit(a, x, y));

  // Dropping into read-only (the server refused a write) lets go of whatever
  // was selected, dragged or being renamed, so no handle outlives the right.
  useEffect(() => {
    if (!readOnly) return;
    setSelectedId(null);
    setDrag(null);
    setEditing(null);
    setHoverDot(null);
    setEdgeHover(null);
  }, [readOnly]);
  const commitEdit = () => {
    if (editing) onUpdate(editing.id, { text: editing.value.trim() });
    setEditing(null);
  };

  // Keep the overlay pixel-aligned with the map, and only while there is
  // something to draw, so idle cost is zero.
  //
  // The map paints inside its own animation frame and reports the paint right
  // there, before the browser composites. A React re-render scheduled from
  // that callback lands a task later — one frame behind the tissue, which is
  // how every shape came to trail a pan or zoom. Committing React synchronously
  // in the callback would align too, but it puts a re-render of every shape on
  // the frame's critical path, and flushes the page's pending effects inside
  // the paint. So the callback does not wait for React: it moves the whole
  // layer with ONE affine transform, from the mapping the shapes were last
  // rendered with to the mapping the map just painted (three probed points fix
  // an affine map), and schedules the re-render. Geometry is exact on every
  // composite. Only screen-space decoration — stroke widths, handles, labels —
  // carries one frame's worth of zoom or turn until React catches up and the
  // layout effect drops the transform. A heavy overlay slows its own catch-up,
  // never the slide.
  const layerRef = useRef<SVGGElement>(null);
  // The mapping THIS render's coordinates came from, probed exactly as the
  // shapes are, and recorded once the render is in the DOM.
  const renderedWith = probeAffine((x, y) => engine.imageToViewport(x, y));
  const renderedRef = useRef<Affine | null>(null);
  useLayoutEffect(() => {
    renderedRef.current = renderedWith;
    layerRef.current?.removeAttribute('transform');
  });
  const hasContent =
    pending != null ||
    poly.length > 0 ||
    annotations.length > 0 ||
    (session?.points.length ?? 0) > 0;
  useEffect(() => {
    if (!hasContent) return;
    return engine.onRender(() => {
      const painted = probeAffine((x, y) => engine.imageToViewport(x, y));
      const rendered = renderedRef.current;
      // A paint that moved nothing — a tile landing on a still view — leaves
      // what is drawn right: nothing to move, nothing to re-render.
      if (painted && rendered && sameAffine(painted, rendered)) return;
      // The view moved under a resting pointer: whatever it was over has gone
      // from there, so its hover closes until the pointer moves again. A
      // tooltip does not follow a moving view: it closes, and one for a row
      // still hovered in the list opens again once the view is still.
      setHover(null);
      setTip(null);
      movedAtRef.current = performance.now();
      setViewMoves((n) => n + 1);
      if (painted && rendered) {
        const back = invertAffine(rendered);
        if (back) layerRef.current?.setAttribute('transform', svgMatrix(multiplyAffine(painted, back)));
      }
      rerender();
    });
  }, [engine, hasContent]);

  useEffect(() => {
    pressRef.current = null;
    setPending(null);
    setPoly([]);
    setCursor(null);
    setHover(null);
  }, [tool, seriesInstanceUID]);
  useEffect(() => {
    if (active) return;
    pressRef.current = null;
    setPending(null);
    setPoly([]);
    setCursor(null);
  }, [active]);

  // Esc leaves the active tool / deselects; Delete removes the selected measure.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'Escape') {
        if (tool === 'polygon' && polyRef.current.length > 0) {
          // Cancel the in-progress polygon but stay armed.
          setPoly([]);
          setCursor(null);
        } else if (tool !== 'none') {
          setPending(null);
          setCursor(null);
          onExitTool?.();
        } else if (selectedId) {
          setSelectedId(null);
        }
      } else if (e.key === 'Enter') {
        if (tool === 'polygon' && polyRef.current.length >= 3) {
          e.preventDefault();
          closePolyRef.current(polyRef.current);
        }
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && !readOnly) {
        // While drawing a polygon, Backspace/Delete undoes the last vertex.
        if (tool === 'polygon' && polyRef.current.length > 0) {
          e.preventDefault();
          setPoly((p) => p.slice(0, -1));
          return;
        }
        // Delete the selected annotation, or the counter being actively edited.
        if (selectedId) {
          e.preventDefault();
          onDelete(selectedId);
          setSelectedId(null);
        } else if (session && (session.id || session.points.length > 0)) {
          e.preventDefault();
          onDeleteSession?.();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [tool, selectedId, onDelete, onExitTool, session, onDeleteSession, readOnly]);

  // The hover over an annotation (who drew it, when): found by where the
  // pointer is over the slide (annotationHover.ts), since somebody else's
  // annotation takes no pointer. Listened for in the capture phase on the
  // viewport around the overlay, which a move over the map or over a shape
  // both pass through; probed once a frame. Nothing while a button is held (a
  // pan, a drag) or for a touch; a press closes it, as on any tooltip.
  useEffect(() => {
    const host = hostRef.current;
    const area = host?.parentElement;
    if (!host || !area) return;
    let frame = 0;
    let last: PointerEvent | null = null;
    const probe = () => {
      frame = 0;
      const e = last;
      if (!e) return;
      const t = e.target as Element | null;
      // Over the slide only: a shape of the overlay, or the map itself — not
      // the zoom bar, the minimap or anything else laid over the slide.
      const overSlide = !!t && (host.contains(t) || !!t.closest('.ol-viewport'));
      const looking = overSlide && !drawingRef.current && e.buttons === 0 && e.pointerType !== 'touch';
      const id = looking ? annotationAt(host, e.clientX, e.clientY) : null;
      if (!id) {
        setHover(null);
        return;
      }
      const r = host.getBoundingClientRect();
      setHover((h) => (h && h.id === id ? h : { id, x: e.clientX - r.left, y: e.clientY - r.top }));
    };
    const onMove = (e: PointerEvent) => {
      last = e;
      if (!frame) frame = requestAnimationFrame(probe);
    };
    const close = () => {
      last = null;
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      setHover(null);
    };
    area.addEventListener('pointermove', onMove, { capture: true, passive: true });
    area.addEventListener('pointerdown', close, { capture: true, passive: true });
    area.addEventListener('wheel', close, { capture: true, passive: true });
    area.addEventListener('pointerleave', close);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      area.removeEventListener('pointermove', onMove, { capture: true });
      area.removeEventListener('pointerdown', close, { capture: true });
      area.removeEventListener('wheel', close, { capture: true });
      area.removeEventListener('pointerleave', close);
    };
  }, []);
  // Opens the moment an annotation is hovered, in the same paint as its
  // highlight — the pointer's here, or on its row in the list, which shows it
  // here as if hovered here (user, 2026-10-10: no delay). Only a view that has
  // just moved holds it back, until the view has been still VIEW_STILL_MS: a
  // tooltip does not follow a moving view, and opening between its frames
  // would flicker. Never while drawing, dragging or naming, and never on the
  // annotation being worked on — the one selected, with its handles and
  // buttons out, or the counter being counted.
  const hoverBlocked = tool !== 'none' || drag !== null || editing !== null;
  const countingId = session?.id ?? null;
  useLayoutEffect(() => {
    if (!hotId || hoverBlocked || hotId === selectedId || hotId === countingId) {
      setTip(null);
      return;
    }
    const still = performance.now() - movedAtRef.current;
    if (still >= VIEW_STILL_MS) {
      setTip(hotId);
      return;
    }
    const t = window.setTimeout(() => setTip(hotId), VIEW_STILL_MS - still);
    return () => window.clearTimeout(t);
  }, [hotId, hoverBlocked, selectedId, countingId, viewMoves]);

  // Deselect a measure when a pointer-down lands outside any annotation element.
  useEffect(() => {
    if (!selectedId) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Element | null;
      if (!t || !t.closest('[data-ann]')) setSelectedId(null);
    };
    // An annotation of another compare pane's overlay is outside this one too,
    // or two panes would each hold a selection and Delete would remove both.
    // Heard on the way down: a press on a shape stops before it bubbles.
    const onOtherShape = (e: PointerEvent) => {
      const t = e.target as Element | null;
      if (t?.closest('[data-ann]') && !hostRef.current?.contains(t)) setSelectedId(null);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('pointerdown', onOtherShape, true);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('pointerdown', onOtherShape, true);
    };
  }, [selectedId]);

  const localPoint = (e: { clientX: number; clientY: number }) => {
    const rect = hostRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  // ── Two-point drawing (measure, arrow, rectangle, ellipse) ──
  // Press, drag and let go: the shape runs from the press to the release. A
  // press let go where it began still places the start, for a second click to
  // finish it (the way these tools used to work).
  const onDrawDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const local = localPoint(e);
    if (!local) return;
    const img = engine.viewportToImage(local.x, local.y);
    if (!img) return;
    capture(e);
    pressRef.current = { x: local.x, y: local.y, fresh: !pending };
    setCursor(local);
    if (!pending) setPending(img);
  };
  const onDrawUp = (e: React.PointerEvent) => {
    const press = pressRef.current;
    pressRef.current = null;
    try {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    } catch {
      // never captured
    }
    const local = localPoint(e);
    if (!press || !pending || !local) return;
    const dragged = Math.hypot(local.x - press.x, local.y - press.y) > DRAG_THRESHOLD_PX;
    if (press.fresh && !dragged) return; // a click: wait for the second one
    const img = engine.viewportToImage(local.x, local.y);
    if (img) finishTwoPoint(pending, img);
  };
  const onDrawCancel = () => {
    pressRef.current = null;
    setPending(null);
    setCursor(null);
  };

  const finishTwoPoint = (start: AnnPoint, img: AnnPoint) => {
    const box = tool === 'rectangle' || tool === 'ellipse';
    const type = tool === 'arrow' ? 'arrow' : tool === 'ellipse' ? 'ellipse' : tool === 'rectangle' ? 'rectangle' : 'measure';
    const created = onCreate({
      study: studyId,
      seriesInstanceUID,
      frameOfReferenceUID: frameOfReferenceUID ?? null,
      type,
      // An arrow's tip is its first point, and it points where it was let go.
      points: box ? bboxCorners(start, img) : type === 'arrow' ? [img, start] : [start, img],
      style: type === 'measure' ? 'measurement' : type,
    });
    setPending(null);
    setCursor(null);
    nameWhenSaved(created);
  };

  // One shape per arming: the tool switches off and the name field opens on the
  // shape just drawn (it needs the saved record's real id, so it waits for it).
  // Drawing another means choosing the tool again.
  const nameWhenSaved = (created: void | Promise<Annotation | undefined>) => {
    onExitTool?.();
    Promise.resolve(created).then((ann) => {
      if (ann) nameShape(ann);
    });
  };

  // The points of `pts` the map can place, in viewport px.
  const toScreen = (pts: AnnPoint[]): Pt[] => pts.map((p) => engine.imageToViewport(p.x, p.y)).filter(isPoint);

  // Where a shape's name label sits on screen, which is where naming it opens.
  const labelAnchor = (a: Annotation): { x: number; y: number } | null => {
    const at = (p: AnnPoint) => engine.imageToViewport(p.x, p.y);
    if ((a.type === 'measure' || a.type === 'arrow') && a.points.length >= 2) {
      const p0 = at(a.points[0]);
      const p1 = at(a.points[1]);
      return p0 && p1 ? { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 - 16 } : null;
    }
    // A region's label is outside it, on the outline as drawn.
    const outside = (ring: AnnPoint[], gap: number) => {
      const s = toScreen(ring);
      return s.length ? labelOutside(s, gap, BADGE_HALF_H) : null;
    };
    if (a.type === 'rectangle') return outside(a.points, BOX_LABEL_GAP);
    if (a.type === 'ellipse') return outside(ellipsePoints(a.points), BOX_LABEL_GAP);
    if (a.type === 'polygon') {
      return outside(isSmoothPolygon(a.style) ? smoothClosedPoints(a.points) : a.points, POLYGON_LABEL_GAP);
    }
    if (a.type === 'counter') return outside(a.points, COUNTER_LABEL_GAP);
    return null;
  };
  const nameShape = (a: Annotation) => {
    if (locked(a)) return;
    const at = labelAnchor(a);
    if (!at) return;
    setSelectedId(a.id);
    startEdit(a, at.x, at.y);
  };
  const nameShapeRef = useRef(nameShape);
  nameShapeRef.current = nameShape;
  useEffect(() => {
    if (!nameRequest) return;
    // Another tool armed since: the name field would steal its keys, so the
    // request lapses (the counter keeps its name, or can be named later).
    if (tool !== 'none') {
      onNameRequestTaken?.();
      return;
    }
    const a = annotations.find((x) => x.id === nameRequest);
    if (!a) return;
    nameShapeRef.current(a);
    onNameRequestTaken?.();
  }, [nameRequest, annotations, tool, onNameRequestTaken]);

  // ── Polygon: multi-click placement. Closes on Enter, a click near the first
  //    vertex, or a quick second click at the same spot (double-click). ──
  const closePoly = (points: AnnPoint[]) => {
    if (points.length < 3) return;
    const created = onCreate({
      study: studyId,
      seriesInstanceUID,
      frameOfReferenceUID: frameOfReferenceUID ?? null,
      type: 'polygon',
      points,
      style: polygonStyle(polygonSmooth),
    });
    setPoly([]);
    setCursor(null);
    lastClickRef.current = null;
    nameWhenSaved(created);
  };
  closePolyRef.current = closePoly;

  const onPolyClick = (e: React.MouseEvent) => {
    const local = localPoint(e);
    if (!local) return;
    const now = Date.now();
    const last = lastClickRef.current;
    lastClickRef.current = { t: now, x: local.x, y: local.y };
    const pts = polyRef.current;
    // A quick second click at ~the same spot reads as a double-click → close.
    if (last && now - last.t < 300 && Math.hypot(local.x - last.x, local.y - last.y) < 6 && pts.length >= 3) {
      closePoly(pts);
      return;
    }
    // Clicking near the first vertex closes the loop.
    if (pts.length >= 3) {
      const first = engine.imageToViewport(pts[0].x, pts[0].y);
      if (first && Math.hypot(first.x - local.x, first.y - local.y) <= 10) {
        closePoly(pts);
        return;
      }
    }
    const img = engine.viewportToImage(local.x, local.y);
    if (img) setPoly((p) => [...p, img]);
  };

  // ── Counter: click drops a point into the active session; clicking an existing
  //    point of the session removes it. ──
  const onCounterClick = (e: React.MouseEvent) => {
    const local = localPoint(e);
    if (!local) return;
    const pts = session?.points ?? [];
    const hit = pts.findIndex((p) => {
      const s = engine.imageToViewport(p.x, p.y);
      return !!s && Math.hypot(s.x - local.x, s.y - local.y) <= 8;
    });
    if (hit >= 0) {
      onCounterRemovePoint?.(hit);
      return;
    }
    const img = engine.viewportToImage(local.x, local.y);
    if (img) onCounterAdd?.(img);
  };

  // Remove a single point from a saved counter (delete the counter if it empties).
  const removeSavedCounterPoint = (a: Annotation, index: number) => {
    const next = a.points.filter((_, i) => i !== index);
    if (next.length === 0) {
      onDelete(a.id);
      setSelectedId(null);
    } else {
      onUpdate(a.id, { points: next });
    }
    setHoverDot(null);
  };

  // Remove a vertex from a polygon (delete the whole shape if it would drop below
  // a triangle) — mirrors the counter point delete.
  const removePolyVertex = (a: Annotation, index: number) => {
    const next = a.points.filter((_, i) => i !== index);
    if (next.length < 3) {
      onDelete(a.id);
      setSelectedId(null);
    } else {
      onUpdate(a.id, { points: next });
    }
    setHoverDot(null);
  };

  // Insert a new vertex at `img` (image space) after vertex `index` — used by the
  // "+" button that sits on each edge's midpoint.
  const insertPolyVertex = (a: Annotation, index: number, img: AnnPoint) => {
    const next = [...a.points];
    next.splice(index + 1, 0, img);
    onUpdate(a.id, { points: next });
    setSelectedId(a.id);
  };

  // ── Measure dragging ──
  const capture = (e: React.PointerEvent) => {
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      // not capturable — drag still works via the element's listeners
    }
  };
  const onHandleDown = (e: React.PointerEvent, ann: Annotation, index: number) => {
    e.stopPropagation();
    setSelectedId(ann.id);
    setDrag({ id: ann.id, kind: 'point', index, point: ann.points[index] });
    capture(e);
  };
  const onLineDown = (e: React.PointerEvent, ann: Annotation) => {
    e.stopPropagation();
    setSelectedId(ann.id);
    const local = localPoint(e);
    const img = local && engine.viewportToImage(local.x, local.y);
    if (img) setDrag({ id: ann.id, kind: 'move', start: img, orig: ann.points, points: ann.points });
    capture(e);
  };
  const onDragMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const local = localPoint(e);
    if (!local) return;
    const img = engine.viewportToImage(local.x, local.y);
    if (!img) return;
    if (drag.kind === 'point') {
      setDrag({ ...drag, point: img });
    } else if (drag.kind === 'move') {
      const ddx = img.x - drag.start.x;
      const ddy = img.y - drag.start.y;
      setDrag({ ...drag, points: drag.orig.map((p) => ({ x: p.x + ddx, y: p.y + ddy })) });
    } else {
      setDrag({ ...drag, points: drag.recompute(img) });
    }
  };

  // ── Box shapes (rectangle / ellipse): move, corner-resize, rotate all reduce
  //    to a recompute() of the corner set from the current pointer. ──
  const onShapeMoveDown = (e: React.PointerEvent, ann: Annotation) => {
    e.stopPropagation();
    setSelectedId(ann.id);
    const local = localPoint(e);
    const start = local && engine.viewportToImage(local.x, local.y);
    if (start) {
      const orig = ann.points;
      setDrag({
        id: ann.id,
        kind: 'shape',
        points: orig,
        recompute: (d) => orig.map((p) => ({ x: p.x + (d.x - start.x), y: p.y + (d.y - start.y) })),
      });
    }
    capture(e);
  };
  const onShapeCornerDown = (e: React.PointerEvent, ann: Annotation, index: number) => {
    e.stopPropagation();
    setSelectedId(ann.id);
    const orig = ann.points;
    setDrag({ id: ann.id, kind: 'shape', points: orig, recompute: (d) => resizeCorner(orig, index, d) });
    capture(e);
  };
  const onShapeRotateDown = (e: React.PointerEvent, ann: Annotation) => {
    e.stopPropagation();
    setSelectedId(ann.id);
    const orig = ann.points;
    const { center, angle: a0, hx, hy } = rectFrame(orig);
    const local = localPoint(e);
    const start = local && engine.viewportToImage(local.x, local.y);
    // Rotate relative to where the pointer was grabbed (no snap): apply the angle
    // the pointer sweeps around the center to the shape's starting angle.
    const t0 = start ? Math.atan2(start.y - center.y, start.x - center.x) : a0;
    setDrag({
      id: ann.id,
      kind: 'shape',
      points: orig,
      recompute: (d) => {
        const t = Math.atan2(d.y - center.y, d.x - center.x);
        return cornersFrom(center, a0 + (t - t0), hx, hy);
      },
    });
    capture(e);
  };
  // Rotate a polygon about its centroid (single rotate handle above the shape).
  const onPolyRotateDown = (e: React.PointerEvent, ann: Annotation) => {
    e.stopPropagation();
    setSelectedId(ann.id);
    const orig = ann.points;
    const c = polygonCentroid(orig);
    const local = localPoint(e);
    const start = local && engine.viewportToImage(local.x, local.y);
    const t0 = start ? Math.atan2(start.y - c.y, start.x - c.x) : 0;
    setDrag({
      id: ann.id,
      kind: 'shape',
      points: orig,
      recompute: (d) => rotatePointsAround(orig, c, Math.atan2(d.y - c.y, d.x - c.x) - t0),
    });
    capture(e);
  };
  const onDragUp = (e: React.PointerEvent) => {
    if (!drag) return;
    try {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    } catch {
      // never captured
    }
    const ann = annotations.find((a) => a.id === drag.id);
    if (ann) {
      const next =
        drag.kind === 'point'
          ? ann.points.map((p, i) => (i === drag.index ? drag.point : p))
          : drag.points;
      if (!pointsEqual(next, ann.points)) onUpdate(ann.id, { points: next });
    }
    setDrag(null);
  };

  const drawing = tool !== 'none';
  const mmPerPx = engine.pixelSpacingMm();

  return (
    <div
      ref={hostRef}
      // ph-no-capture, like the map beneath it (lib/analytics.ts): drawing,
      // counting and a shape's handles and labels are not analytics clicks.
      className="ph-no-capture absolute inset-0 overflow-hidden"
      style={{ pointerEvents: 'none' }}
      // The overlay sits over the map, so a wheel over a shape, a label or the
      // drawing layer never reaches it: hand it to the view's scroll gesture,
      // as the map does. A pinch (Ctrl+wheel) is zoomed by the viewport before
      // it gets here.
      onWheel={(e) => {
        if (e.ctrlKey) return;
        engine.scrollZoom(e.nativeEvent, { x: e.clientX, y: e.clientY });
      }}
    >
      {/* Capture layer for placing annotations. */}
      {drawing && (
        <div
          className="absolute inset-0 cursor-crosshair"
          // touch-action: a finger drags out a shape rather than scrolling the page.
          style={{ pointerEvents: 'auto', touchAction: 'none' }}
          {...(tool === 'counter' || tool === 'polygon'
            ? { onClick: tool === 'counter' ? onCounterClick : onPolyClick }
            : { onPointerDown: onDrawDown, onPointerUp: onDrawUp, onPointerCancel: onDrawCancel })}
          onPointerMove={(e) => setCursor(localPoint(e))}
        />
      )}

      {/* overflow-hidden clips geometry to the image area, never over the panels. */}
      <svg className="absolute inset-0 h-full w-full overflow-hidden" style={{ pointerEvents: 'none' }}>
        {/* The movable layer: the paint callback above shifts it onto each new
            frame with one transform, which the next React render drops. */}
        <g ref={layerRef}>
          {/* Measures */}
          {annotations.map((a) => {
            if (a.type !== 'measure' || a.points.length < 2) return null;
            const pts =
              drag && drag.id === a.id
                ? drag.kind === 'point'
                  ? a.points.map((p, i) => (i === drag.index ? drag.point : p))
                  : drag.points
                : a.points;
            const p0 = engine.imageToViewport(pts[0].x, pts[0].y);
            const p1 = engine.imageToViewport(pts[1].x, pts[1].y);
            if (!p0 || !p1) return null;
            const selected = a.id === selectedId;
            const hot = a.id === hotId;
            const color = colorForStyle(a.style);
            const label = measureLabel(
              a.text,
              formatLength(Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y), mmPerPx),
            );
            const mx = (p0.x + p1.x) / 2;
            const my = (p0.y + p1.y) / 2;
            const badgeW = label.length * 6.4 + 14;
            return (
              <g key={a.id}>
                <line x1={p0.x} y1={p0.y} x2={p1.x} y2={p1.y} stroke={HALO} strokeWidth={hot ? 7 : 5} />
                <line
                  x1={p0.x}
                  y1={p0.y}
                  x2={p1.x}
                  y2={p1.y}
                  stroke={color}
                  strokeWidth={hot ? 3 : 2}
                  strokeDasharray={selected ? '5 4' : undefined}
                />
                <line
                  data-ann
                  data-hover={a.id}
                  x1={p0.x}
                  y1={p0.y}
                  x2={p1.x}
                  y2={p1.y}
                  stroke="transparent"
                  strokeWidth={14}
                  style={{ pointerEvents: inert(a) ? 'none' : 'stroke', cursor: 'move' }}
                  onPointerDown={(e) => onLineDown(e, a)}
                  onPointerMove={onDragMove}
                  onPointerUp={onDragUp}
                />
                {[p0, p1].map((p, i) => (
                  <circle
                    data-ann
                    data-hover={a.id}
                    key={i}
                    cx={p.x}
                    cy={p.y}
                    r={selected ? 6 : hot ? 5 : 4}
                    fill={color}
                    stroke={HALO}
                    strokeWidth={1.5}
                    style={{ pointerEvents: inert(a) ? 'none' : 'auto', cursor: 'grab' }}
                    onPointerDown={(e) => onHandleDown(e, a, i)}
                    onPointerMove={onDragMove}
                    onPointerUp={onDragUp}
                  />
                ))}
                {editing?.id !== a.id && (
                  <g transform={`translate(${mx}, ${my - 16})`}>
                    <Badge
                      x={0}
                      y={0}
                      text={label}
                      color={color}
                      onSelect={selectOn(a)}
                      hover={a.id}
                      hot={hot}
                      onRename={renameOn(a, mx, my - 16)}
                    />
                    {selected && (
                      <>
                        <LabelButton x={-badgeW / 2 - 13} color={color} title="Rename" Icon={Pencil}
                          onClick={() => startEdit(a, mx, my - 16)} />
                        <LabelButton x={badgeW / 2 + 13} color={color} title="Delete measurement" Icon={Trash2}
                          onClick={() => { onDelete(a.id); setSelectedId(null); }} />
                      </>
                    )}
                  </g>
                )}
              </g>
            );
          })}

          {/* Arrows — a line with a filled head; optional name label. */}
          {annotations.map((a) => {
            if (a.type !== 'arrow' || a.points.length < 2) return null;
            const pts =
              drag && drag.id === a.id
                ? drag.kind === 'point'
                  ? a.points.map((p, i) => (i === drag.index ? drag.point : p))
                  : drag.points
                : a.points;
            const p0 = engine.imageToViewport(pts[0].x, pts[0].y);
            const p1 = engine.imageToViewport(pts[1].x, pts[1].y);
            if (!p0 || !p1) return null;
            const selected = a.id === selectedId;
            const hot = a.id === hotId;
            const color = colorForStyle('arrow');
            // Tip is the first point (p0); the shaft runs from the tail (p1) to it.
            const ang = Math.atan2(p0.y - p1.y, p0.x - p1.x);
            const ah = 13;
            const spread = Math.PI / 7;
            const h1 = { x: p0.x - ah * Math.cos(ang - spread), y: p0.y - ah * Math.sin(ang - spread) };
            const h2 = { x: p0.x - ah * Math.cos(ang + spread), y: p0.y - ah * Math.sin(ang + spread) };
            const showLabel = selected || !!(a.text && a.text.trim());
            const label = arrowLabel(a.text);
            const mx = (p0.x + p1.x) / 2;
            const my = (p0.y + p1.y) / 2;
            const badgeW = label.length * 6.4 + 14;
            return (
              <g key={a.id}>
                <line x1={p0.x} y1={p0.y} x2={p1.x} y2={p1.y} stroke={HALO} strokeWidth={hot ? 7 : 5} />
                <line
                  x1={p0.x}
                  y1={p0.y}
                  x2={p1.x}
                  y2={p1.y}
                  stroke={color}
                  strokeWidth={hot ? 3 : 2}
                  strokeDasharray={selected ? '5 4' : undefined}
                />
                {/* Move-whole-arrow hit target (drawn first so the endpoint handles
                    below sit on top of it near the ends). */}
                <line
                  data-ann
                  data-hover={a.id}
                  x1={p0.x}
                  y1={p0.y}
                  x2={p1.x}
                  y2={p1.y}
                  stroke="transparent"
                  strokeWidth={14}
                  style={{ pointerEvents: inert(a) ? 'none' : 'stroke', cursor: 'move' }}
                  onPointerDown={(e) => onLineDown(e, a)}
                  onPointerMove={onDragMove}
                  onPointerUp={onDragUp}
                />
                {/* Arrowhead at the tip (p0) — also the drag handle for that end. */}
                <polygon
                  data-ann
                  data-hover={a.id}
                  points={`${p0.x},${p0.y} ${h1.x},${h1.y} ${h2.x},${h2.y}`}
                  fill={color}
                  stroke={HALO}
                  strokeWidth={1}
                  strokeLinejoin="round"
                  style={{ pointerEvents: inert(a) ? 'none' : 'auto', cursor: 'grab' }}
                  onPointerDown={(e) => onHandleDown(e, a, 0)}
                  onPointerMove={onDragMove}
                  onPointerUp={onDragUp}
                />
                {/* Circle handle only at the tail (p1). */}
                <circle
                  data-ann
                  data-hover={a.id}
                  cx={p1.x}
                  cy={p1.y}
                  r={selected ? 6 : hot ? 5 : 4}
                  fill={color}
                  stroke={HALO}
                  strokeWidth={1.5}
                  style={{ pointerEvents: inert(a) ? 'none' : 'auto', cursor: 'grab' }}
                  onPointerDown={(e) => onHandleDown(e, a, 1)}
                  onPointerMove={onDragMove}
                  onPointerUp={onDragUp}
                />
                {showLabel && editing?.id !== a.id && (
                  <g transform={`translate(${mx}, ${my - 16})`}>
                    <Badge
                      x={0}
                      y={0}
                      text={label}
                      color={color}
                      onSelect={selectOn(a)}
                      hover={a.id}
                      hot={hot}
                      onRename={renameOn(a, mx, my - 16)}
                    />
                    {selected && (
                      <>
                        <LabelButton x={-badgeW / 2 - 13} color={color} title="Rename" Icon={Pencil}
                          onClick={() => startEdit(a, mx, my - 16)} />
                        <LabelButton x={badgeW / 2 + 13} color={color} title="Delete arrow" Icon={Trash2}
                          onClick={() => { onDelete(a.id); setSelectedId(null); }} />
                      </>
                    )}
                  </g>
                )}
              </g>
            );
          })}

          {/* Box shapes: rectangle + ellipse (4 corners, possibly rotated). */}
          {annotations.map((a) => {
            if ((a.type !== 'rectangle' && a.type !== 'ellipse') || a.points.length < 4) return null;
            const pts = drag && drag.id === a.id && drag.kind === 'shape' ? drag.points : a.points;
            const screen = pts.map((p) => engine.imageToViewport(p.x, p.y));
            if (screen.some((s) => !s)) return null;
            const sc = screen as { x: number; y: number }[];
            const selected = a.id === selectedId;
            const hot = a.id === hotId;
            const color = colorForStyle(a.style);
            const frame = rectFrame(pts);
            const area = a.type === 'ellipse' ? Math.PI * frame.hx * frame.hy : frame.w * frame.h;
            const label = measureLabel(a.text, formatArea(area, mmPerPx)); // name and area
            let ring = sc; // the outline as drawn: the corners, or the ellipse through them
            if (a.type === 'ellipse') {
              const es = toScreen(ellipsePoints(pts));
              if (es.length > 2) ring = es;
            }
            const outline = `M${ring.map((s) => `${s.x},${s.y}`).join(' L')} Z`;
            const cs = { x: (sc[0].x + sc[2].x) / 2, y: (sc[0].y + sc[2].y) / 2 };
            const lab = labelOutside(ring, BOX_LABEL_GAP, BADGE_HALF_H);
            const badgeW = label.length * 6.4 + 14;
            // Screen angle of the shape's top edge — cursors are offset from this so
            // they keep a fixed 45° orientation relative to each corner as it rotates.
            const theta = Math.atan2(sc[1].y - sc[0].y, sc[1].x - sc[0].x);
            // Width on the top edge, height on the left edge (upright labels).
            const topMid = { x: (sc[0].x + sc[1].x) / 2, y: (sc[0].y + sc[1].y) / 2 };
            const leftMid = { x: (sc[0].x + sc[3].x) / 2, y: (sc[0].y + sc[3].y) / 2 };
            const widthText = formatLength(frame.w, mmPerPx);
            const heightText = formatLength(frame.h, mmPerPx);
            return (
              <g key={a.id}>
                <path
                  data-ann
                  data-hover={a.id}
                  d={outline}
                  fill={color}
                  fillOpacity={hot ? 0.16 : 0.08}
                  stroke="none"
                  style={{ pointerEvents: inert(a) ? 'none' : 'auto', cursor: 'move' }}
                  onPointerDown={(e) => onShapeMoveDown(e, a)}
                  onPointerMove={onDragMove}
                  onPointerUp={onDragUp}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    startEdit(a, lab.x, lab.y);
                  }}
                />
                <path d={outline} fill="none" stroke={HALO} strokeWidth={hot ? 7 : 5} style={{ pointerEvents: 'none' }} />
                <path
                  d={outline}
                  fill="none"
                  stroke={color}
                  strokeWidth={hot ? 3 : 2}
                  strokeDasharray={selected ? '5 4' : undefined}
                  style={{ pointerEvents: 'none' }}
                />
                {/* Dimensions on the edges, running along each axis (kept readable). */}
                <MiniLabel
                  x={topMid.x}
                  y={topMid.y}
                  text={widthText}
                  color={color}
                  rotate={readableAngle((theta * 180) / Math.PI)}
                />
                <MiniLabel
                  x={leftMid.x}
                  y={leftMid.y}
                  text={heightText}
                  color={color}
                  rotate={readableAngle((theta * 180) / Math.PI - 90)}
                />
                {selected && (
                  <>
                    {/* Rotate zones: clear of the corner handles (curved-arrow cursor). */}
                    {sc.map((p, i) => {
                      const ox = p.x - cs.x;
                      const oy = p.y - cs.y;
                      const ol = Math.hypot(ox, oy) || 1;
                      const rz = { x: p.x + (20 * ox) / ol, y: p.y + (20 * oy) / ol };
                      return (
                        <circle
                          data-ann
                          key={`rot-${i}`}
                          cx={rz.x}
                          cy={rz.y}
                          r={12}
                          fill="transparent"
                          style={{
                            pointerEvents: 'auto',
                            cursor: rotateCursor(theta + (i * Math.PI) / 2 + Math.PI / 4),
                          }}
                          onPointerDown={(e) => onShapeRotateDown(e, a)}
                          onPointerMove={onDragMove}
                          onPointerUp={onDragUp}
                        />
                      );
                    })}
                    {/* Corner resize handles — double-arrow pointing at the center. */}
                    {sc.map((p, i) => (
                      <circle
                        data-ann
                        key={i}
                        cx={p.x}
                        cy={p.y}
                        r={5}
                        fill={color}
                        stroke={HALO}
                        strokeWidth={1.5}
                        style={{
                          pointerEvents: 'auto',
                          cursor: resizeCursor(theta + (i % 2 === 0 ? Math.PI / 4 : (3 * Math.PI) / 4)),
                        }}
                        onPointerDown={(e) => onShapeCornerDown(e, a, i)}
                        onPointerMove={onDragMove}
                        onPointerUp={onDragUp}
                      />
                    ))}
                  </>
                )}
                {editing?.id !== a.id && (
                  <g transform={`translate(${lab.x}, ${lab.y})`}>
                    <Badge
                      x={0}
                      y={0}
                      text={label}
                      color={color}
                      onSelect={selectOn(a)}
                      hover={a.id}
                      hot={hot}
                      onRename={renameOn(a, lab.x, lab.y)}
                    />
                    {selected && (
                      <>
                        <LabelButton x={-badgeW / 2 - 13} color={color} title="Rename" Icon={Pencil}
                          onClick={() => startEdit(a, lab.x, lab.y)} />
                        <LabelButton x={badgeW / 2 + 13} color={color} title="Delete" Icon={Trash2}
                          onClick={() => { onDelete(a.id); setSelectedId(null); }} />
                      </>
                    )}
                  </g>
                )}
              </g>
            );
          })}

          {/* Polygons: closed multi-vertex regions. Move (drag body), reshape (drag
              a vertex), rotate (zones just outside each vertex); the badge above it
              shows the shoelace area. */}
          {annotations.map((a) => {
            if (a.type !== 'polygon' || a.points.length < 3) return null;
            const pts =
              drag && drag.id === a.id
                ? drag.kind === 'point'
                  ? a.points.map((p, i) => (i === drag.index ? drag.point : p))
                  : drag.points
                : a.points;
            const screen = pts.map((p) => engine.imageToViewport(p.x, p.y));
            if (screen.some((s) => !s)) return null;
            const sc = screen as { x: number; y: number }[];
            const selected = a.id === selectedId;
            const hot = a.id === hotId;
            const color = colorForStyle('polygon');
            const smooth = isSmoothPolygon(a.style);
            // Editable vertices (`sc`) vs. the drawn outline (`so`) — a sampled
            // centripetal Catmull-Rom spline when smooth, else the vertices.
            const outlinePts = smooth ? smoothClosedPoints(pts) : pts;
            const so = toScreen(outlinePts);
            const outline =
              so.length >= 3
                ? `M${so.map((s) => `${s.x},${s.y}`).join(' L')} Z`
                : `M${sc.map((s) => `${s.x},${s.y}`).join(' L')} Z`;
            const ctr = polygonCentroid(outlinePts);
            const cs = engine.imageToViewport(ctr.x, ctr.y) ?? sc[0];
            // Area and label place come from the actual drawn outline, so the badge
            // always matches the shape on screen (straight vertices or smoothed curve).
            const label = measureLabel(a.text, formatArea(polygonArea(outlinePts), mmPerPx));
            const lab = labelOutside(so.length >= 3 ? so : sc, POLYGON_LABEL_GAP, BADGE_HALF_H);
            const badgeW = label.length * 6.4 + 14;
            return (
              <g key={a.id}>
                <path
                  data-ann
                  data-hover={a.id}
                  d={outline}
                  fill={color}
                  fillOpacity={hot ? 0.16 : 0.08}
                  stroke="none"
                  style={{ pointerEvents: inert(a) ? 'none' : 'auto', cursor: 'move' }}
                  onPointerDown={(e) => onShapeMoveDown(e, a)}
                  onPointerMove={onDragMove}
                  onPointerUp={onDragUp}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    startEdit(a, lab.x, lab.y);
                  }}
                />
                <path d={outline} fill="none" stroke={HALO} strokeWidth={hot ? 7 : 5} style={{ pointerEvents: 'none' }} />
                <path
                  d={outline}
                  fill="none"
                  stroke={color}
                  strokeWidth={hot ? 3 : 2}
                  strokeDasharray={selected ? '5 4' : undefined}
                  style={{ pointerEvents: 'none' }}
                />
                {selected && (
                  <>
                    {/* Add-node target: a transparent band over the outline. Hover it
                        to preview a "+" that rides along the visible line (curve when
                        smooth); click to insert a vertex there. */}
                    <path
                      data-ann
                      d={outline}
                      fill="none"
                      stroke="transparent"
                      strokeWidth={16}
                      style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                      onPointerMove={(e) => {
                        const local = localPoint(e);
                        if (!local || so.length < 2) return;
                        const q = nearestOnClosed(local, so);
                        if (q) setEdgeHover({ id: a.id, x: q.x, y: q.y, seg: Math.floor((q.i * sc.length) / so.length) });
                      }}
                      onPointerLeave={() => setEdgeHover((h) => (h && h.id === a.id ? null : h))}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        const local = localPoint(e);
                        if (!local || so.length < 2) return;
                        const q = nearestOnClosed(local, so);
                        if (!q) return;
                        const img = engine.viewportToImage(q.x, q.y);
                        if (img) insertPolyVertex(a, Math.floor((q.i * sc.length) / so.length), img);
                        setEdgeHover(null);
                      }}
                    />
                    {/* "+" indicator riding the outline under the cursor. */}
                    {edgeHover && edgeHover.id === a.id && (
                      <g transform={`translate(${edgeHover.x}, ${edgeHover.y})`} style={{ pointerEvents: 'none' }}>
                        <circle r={7} fill="rgba(11,11,11,0.8)" stroke={color} strokeWidth={1.5} />
                        <line x1={-3.5} y1={0} x2={3.5} y2={0} stroke={color} strokeWidth={1.5} strokeLinecap="round" />
                        <line x1={0} y1={-3.5} x2={0} y2={3.5} stroke={color} strokeWidth={1.5} strokeLinecap="round" />
                      </g>
                    )}
                    {/* Rotate zones just outside each vertex (curved-arrow cursor),
                        matching the rectangle/ellipse rotation. */}
                    {sc.map((p, i) => {
                      const ox = p.x - cs.x;
                      const oy = p.y - cs.y;
                      const ol = Math.hypot(ox, oy) || 1;
                      const rz = { x: p.x + (18 * ox) / ol, y: p.y + (18 * oy) / ol };
                      return (
                        <circle
                          data-ann
                          key={`rot-${i}`}
                          cx={rz.x}
                          cy={rz.y}
                          r={11}
                          fill="transparent"
                          style={{ pointerEvents: 'auto', cursor: rotateCursor(Math.atan2(oy, ox) + Math.PI) }}
                          onPointerDown={(e) => onPolyRotateDown(e, a)}
                          onPointerMove={onDragMove}
                          onPointerUp={onDragUp}
                        />
                      );
                    })}
                    {/* Vertex handles — drag to reshape; hover to reveal a delete (X),
                        same as counter points. */}
                    {sc.map((p, i) => {
                      const hovering = hoverDot?.id === a.id && hoverDot.index === i;
                      // Delete badge to the right of the handle, with a small gap. Hover
                      // is tracked on the group (enter/leave fire only at the subtree
                      // boundary) and a transparent bridge spans the gap, so moving from
                      // the handle to the badge never drops the hover.
                      const del = { x: p.x + 15, y: p.y };
                      return (
                        <g
                          key={i}
                          onPointerEnter={() => setHoverDot({ id: a.id, index: i })}
                          onPointerLeave={() =>
                            setHoverDot((h) => (h && h.id === a.id && h.index === i ? null : h))
                          }
                        >
                          {hovering && (
                            <rect
                              data-ann
                              x={p.x - 6}
                              y={p.y - 9}
                              width={29}
                              height={18}
                              fill="transparent"
                              style={{ pointerEvents: 'auto' }}
                            />
                          )}
                          <circle
                            data-ann
                            cx={p.x}
                            cy={p.y}
                            r={5}
                            fill={color}
                            stroke={HALO}
                            strokeWidth={1.5}
                            style={{ pointerEvents: 'auto', cursor: 'grab' }}
                            onPointerDown={(e) => onHandleDown(e, a, i)}
                            onPointerMove={onDragMove}
                            onPointerUp={onDragUp}
                          />
                          {hovering && (
                            <g
                              data-ann
                              transform={`translate(${del.x}, ${del.y})`}
                              style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                removePolyVertex(a, i);
                              }}
                            >
                              <title>Delete point</title>
                              <circle r={8} fill="rgba(11,11,11,0.9)" stroke={color} strokeWidth={1.5} />
                              <X x={-5} y={-5} width={10} height={10} color={color} />
                            </g>
                          )}
                        </g>
                      );
                    })}
                  </>
                )}
                {editing?.id !== a.id && (
                  <g transform={`translate(${lab.x}, ${lab.y})`}>
                    <Badge
                      x={0}
                      y={0}
                      text={label}
                      color={color}
                      onSelect={selectOn(a)}
                      hover={a.id}
                      hot={hot}
                      onRename={renameOn(a, lab.x, lab.y)}
                    />
                    {selected && (
                      <>
                        <LabelButton x={-badgeW / 2 - 13} color={color} title="Rename" Icon={Pencil}
                          onClick={() => startEdit(a, lab.x, lab.y)} />
                        <LabelButton x={badgeW / 2 + 13} color={color} title="Delete" Icon={Trash2}
                          onClick={() => { onDelete(a.id); setSelectedId(null); }} />
                      </>
                    )}
                  </g>
                )}
              </g>
            );
          })}

          {/* Saved counters (the one being edited is shown via `session` instead).
              Hover a dot to reveal a delete button to its right; click the label to
              select the counter (double-click to rename); the trash by the label
              removes the whole counter. */}
          {annotations.map((a) => {
            if (a.type !== 'counter' || a.points.length === 0 || a.id === session?.id) return null;
            const color = colorForStyle(a.style);
            const selected = a.id === selectedId;
            const hot = a.id === hotId;
            const dots = a.points.map((p) => engine.imageToViewport(p.x, p.y));
            const shown = dots.filter(isPoint);
            if (!shown.length) return null;
            const c = labelOutside(shown, COUNTER_LABEL_GAP, BADGE_HALF_H);
            const label = counterLabel(a.text, a.points.length);
            const badgeW = label.length * 6.4 + 14;
            return (
              <g key={a.id}>
                {a.points.map((_, i) => {
                  const s = dots[i];
                  if (!s) return null;
                  // A point's delete shows only on the selected counter: an
                  // unselected one is picked first (a press on any dot). As on a
                  // polygon's vertices, hover is tracked on the group and a
                  // transparent bridge spans the gap to the badge, so moving onto
                  // it never drops the hover.
                  const deletable =
                    selected && hoverDot?.id === a.id && hoverDot.index === i;
                  return (
                    <g
                      key={i}
                      onPointerEnter={inert(a) ? undefined : () => setHoverDot({ id: a.id, index: i })}
                      onPointerLeave={
                        inert(a)
                          ? undefined
                          : () => setHoverDot((h) => (h && h.id === a.id && h.index === i ? null : h))
                      }
                    >
                      {deletable && (
                        <rect
                          data-ann
                          x={s.x - 6}
                          y={s.y - 8}
                          width={26}
                          height={16}
                          fill="transparent"
                          style={{ pointerEvents: 'auto' }}
                        />
                      )}
                      <circle
                        data-ann
                        data-hover={a.id}
                        cx={s.x}
                        cy={s.y}
                        r={selected || hot ? 5 : 4}
                        fill={color}
                        stroke={HALO}
                        strokeWidth={1.5}
                        style={{ pointerEvents: inert(a) ? 'none' : 'auto', cursor: 'pointer' }}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          setSelectedId(a.id);
                        }}
                      />
                      {deletable && (
                        <g
                          data-ann
                          transform={`translate(${s.x + 13}, ${s.y})`}
                          style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            removeSavedCounterPoint(a, i);
                          }}
                        >
                          <title>Delete point</title>
                          <circle r={7} fill="rgba(11,11,11,0.85)" stroke={color} strokeWidth={1} />
                          <X x={-4.5} y={-4.5} width={9} height={9} color={color} />
                        </g>
                      )}
                    </g>
                  );
                })}
                {editing?.id !== a.id && (
                  <g transform={`translate(${c.x}, ${c.y})`}>
                    <Badge
                      x={0}
                      y={0}
                      text={label}
                      color={color}
                      onSelect={selectOn(a)}
                      hover={a.id}
                      hot={hot}
                      onRename={renameOn(a, c.x, c.y)}
                    />
                    {selected && (
                      <>
                        <LabelButton x={-badgeW / 2 - 13} color={color} title="Rename" Icon={Pencil}
                          onClick={() => startEdit(a, c.x, c.y)} />
                        <LabelButton x={badgeW / 2 + 13} color={color} title="Delete counter" Icon={Trash2}
                          onClick={() => { onDelete(a.id); setSelectedId(null); }} />
                      </>
                    )}
                  </g>
                )}
              </g>
            );
          })}

          {/* Active counting session (live). */}
          {session && session.points.length > 0 && (() => {
            const color = COUNTER_PALETTE[session.colorIndex % COUNTER_PALETTE.length];
            const dots = session.points.map((p) => engine.imageToViewport(p.x, p.y));
            const shown = dots.filter(isPoint);
            const c = shown.length ? labelOutside(shown, COUNTER_LABEL_GAP, BADGE_HALF_H) : null;
            // A counter resumed keeps its name while it is counted.
            const name = session.id ? annotations.find((x) => x.id === session.id)?.text : undefined;
            return (
              <g>
                {session.points.map((_, i) => {
                  const s = dots[i];
                  return s ? (
                    <CounterMarker key={i} x={s.x} y={s.y} color={color} interactive={false} />
                  ) : null;
                })}
                {c && <Badge x={c.x} y={c.y} text={counterLabel(name, session.points.length)} color={color} />}
              </g>
            );
          })()}

          {/* Live preview of a box shape being drawn. */}
          {pending && cursor && (tool === 'rectangle' || tool === 'ellipse') && (() => {
            const cImg = engine.viewportToImage(cursor.x, cursor.y);
            if (!cImg) return null;
            const corners = bboxCorners(pending, cImg);
            const src = tool === 'ellipse' ? ellipsePoints(corners) : corners;
            const s = toScreen(src);
            if (s.length < 3) return null;
            const d = `M${s.map((q) => `${q.x},${q.y}`).join(' L')} Z`;
            const c = colorForStyle(tool);
            return (
              <g>
                <path d={d} fill="none" stroke={HALO} strokeWidth={5} />
                <path d={d} fill="none" stroke={c} strokeWidth={2} strokeDasharray="5 4" />
              </g>
            );
          })()}

          {/* Live preview of a polygon under construction: placed edges, a dashed
              rubber-band to the cursor, and a faint closing segment to vertex 0. */}
          {tool === 'polygon' && poly.length > 0 && (() => {
            const sc = toScreen(poly);
            if (sc.length === 0) return null;
            const color = colorForStyle('polygon');
            const placed = sc.length > 1 ? `M${sc.map((s) => `${s.x},${s.y}`).join(' L')}` : null;
            const last = sc[sc.length - 1];
            const nearFirst =
              sc.length >= 3 && !!cursor && Math.hypot(sc[0].x - cursor.x, sc[0].y - cursor.y) <= 10;
            return (
              <g>
                {placed && <path d={placed} fill="none" stroke={HALO} strokeWidth={5} />}
                {placed && <path d={placed} fill="none" stroke={color} strokeWidth={2} />}
                {cursor && (
                  <line x1={last.x} y1={last.y} x2={cursor.x} y2={cursor.y} stroke={color} strokeWidth={2} strokeDasharray="5 4" />
                )}
                {cursor && sc.length >= 2 && (
                  <line x1={cursor.x} y1={cursor.y} x2={sc[0].x} y2={sc[0].y} stroke={color} strokeWidth={1} strokeDasharray="2 5" opacity={0.6} />
                )}
                {sc.map((s, i) => (
                  <circle
                    key={i}
                    cx={s.x}
                    cy={s.y}
                    r={i === 0 && nearFirst ? 7 : 4}
                    fill={i === 0 && !nearFirst ? 'white' : color}
                    stroke={HALO}
                    strokeWidth={1.5}
                  />
                ))}
              </g>
            );
          })()}

          {/* Live preview of the measure/arrow being drawn (arrowhead at the tip,
              which is the pointer: an arrow points where it is let go). */}
          {pending && cursor && (tool === 'measure' || tool === 'arrow') && (() => {
            const start = engine.imageToViewport(pending.x, pending.y);
            if (!start) return null;
            const arrow = tool === 'arrow';
            const previewColor = arrow ? colorForStyle('arrow') : '#fbbf24';
            let head = null;
            if (arrow) {
              const ang = Math.atan2(cursor.y - start.y, cursor.x - start.x);
              const ah = 13;
              const spread = Math.PI / 7;
              const h1 = { x: cursor.x - ah * Math.cos(ang - spread), y: cursor.y - ah * Math.sin(ang - spread) };
              const h2 = { x: cursor.x - ah * Math.cos(ang + spread), y: cursor.y - ah * Math.sin(ang + spread) };
              head = (
                <polygon
                  points={`${cursor.x},${cursor.y} ${h1.x},${h1.y} ${h2.x},${h2.y}`}
                  fill={previewColor}
                  stroke={HALO}
                  strokeWidth={1}
                  strokeLinejoin="round"
                />
              );
            }
            return (
              <g>
                <line x1={start.x} y1={start.y} x2={cursor.x} y2={cursor.y} stroke={HALO} strokeWidth={5} />
                <line
                  x1={start.x}
                  y1={start.y}
                  x2={cursor.x}
                  y2={cursor.y}
                  stroke={previewColor}
                  strokeWidth={2}
                  strokeDasharray="5 4"
                />
                {head}
              </g>
            );
          })()}
        </g>
      </svg>

      {/* The hover over an annotation: the app's tooltip, opened by hand on a
          point that stands in for its label (or the pointer, when the label is
          out of view), so it sits just above the annotation's name. From a
          row in the list there is no pointer here: a label out of view opens
          nothing. */}
      {(() => {
        const a = tip ? annotations.find((x) => x.id === tip) : undefined;
        if (!a) return null;
        const label = labelAnchor(a);
        const host = hostRef.current;
        const inView =
          !!label && !!host && label.x >= 0 && label.y >= 0 && label.x <= host.clientWidth && label.y <= host.clientHeight;
        const pointer = hover && hover.id === a.id ? hover : null;
        // A label is a pill about its anchor: the tooltip goes on its top edge.
        const at = inView ? { x: label!.x, y: label!.y - BADGE_HALF_H } : pointer ? { x: pointer.x, y: pointer.y - 6 } : null;
        if (!at) return null;
        // Keyed by the annotation: the tooltip places itself on its anchor when
        // it opens and does not follow the anchor moving, so moving straight
        // on to the next annotation opens a new one there.
        return (
          <Tooltip key={a.id} open onOpenChange={(open) => !open && setHover(null)}>
            <TooltipTrigger asChild>
              <span aria-hidden className="absolute h-0 w-0" style={{ left: at.x, top: at.y }} />
            </TooltipTrigger>
            <TooltipContent side="top" className="pointer-events-none">
              <AnnotationByline annotation={a} />
            </TooltipContent>
          </Tooltip>
        );
      })()}

      {/* Inline rename: an HTML input positioned over the label being edited,
          kept in the view when the label is out of it (a region larger than the
          view, double-clicked inside). */}
      {editing && (
        <input
          ref={nameRef}
          autoFocus
          value={editing.value}
          placeholder="Name"
          onChange={(e) => setEditing({ ...editing, value: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitEdit();
            else if (e.key === 'Escape') setEditing(null);
          }}
          onBlur={commitEdit}
          className="absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded border border-white/40 bg-black/85 px-1.5 py-0.5 text-center text-xs text-white outline-none"
          style={{
            left: keepInView(editing.x, hostRef.current?.clientWidth, nameSize.w / 2 + NAME_FIELD_INSET),
            top: keepInView(editing.y, hostRef.current?.clientHeight, nameSize.h / 2 + NAME_FIELD_INSET),
            pointerEvents: 'auto',
            width: `max(5rem, ${editing.value.length + 2}ch)`,
          }}
        />
      )}
    </div>
  );
}
